import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { ProjectStatus } from '@paladin-monorepo/common';

interface TabLink {
  to: string;
  label: string;
  exact?: boolean;
  visible?: boolean;
}

export function ProjectTabs({
  basePath,
  projectStatus,
  hasOffer = true,
  governed = false,
}: {
  basePath: string;
  projectStatus: ProjectStatus;
  hasOffer?: boolean;
  /** The project has PRINCE2 governance: the tolerance and cost tabs are shown. */
  governed?: boolean;
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const paymentsStatuses: ProjectStatus[] = ['Kickoff', 'InProgress', 'Closure'];

  const links: TabLink[] = [
    { to: basePath, label: t('workspace.tabs.overview'), exact: true },
    { to: `${basePath}/milestones`, label: t('workspace.tabs.milestones') },
    { to: `${basePath}/documents`, label: t('workspace.tabs.documents') },
    { to: `${basePath}/contracts`, label: t('workspace.tabs.contracts'), visible: hasOffer },
    { to: `${basePath}/payments`, label: t('workspace.tabs.payments'), visible: hasOffer && paymentsStatuses.includes(projectStatus) },
    { to: `${basePath}/tolerances`, label: t('workspace.tabs.tolerances'), visible: governed },
    { to: `${basePath}/costs`, label: t('workspace.tabs.costs'), visible: governed },
    { to: `${basePath}/risks`, label: t('workspace.tabs.risks'), visible: governed },
    { to: `${basePath}/measurements`, label: t('workspace.tabs.measurements'), visible: governed },
    { to: `${basePath}/changes`, label: t('workspace.tabs.changes'), visible: governed },
    { to: `${basePath}/business-case`, label: t('workspace.tabs.businessCase'), visible: governed },
    { to: `${basePath}/log`, label: t('workspace.tabs.log'), visible: governed },
  ];

  const visibleLinks = links.filter((l) => l.visible !== false);

  const isActive = (to: string, exact?: boolean) => {
    if (exact) {
      return pathname === to;
    }
    return pathname.startsWith(to);
  };

  return (
    <nav className="border-b mb-4">
      <ul className="flex -mb-px text-sm font-medium">
        {visibleLinks.map(({ to, label, exact }) => (
          <li className="mr-4" key={to}>
            <Link
              to={to}
              className={[
                'inline-block p-3 border-b-2 rounded-t-md',
                isActive(to, exact)
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >
              {label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
